import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  useToast,
  Select,
  SimpleGrid,
  Switch,
  CheckboxGroup,
  Checkbox,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

const ROLES = ["Creator", "Admin", "Master", "Copyrights Holder", "Manager"];

const EditUserModal = ({ isOpen, onClose, user, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const toast = useToast();
  const isEditing = !!user;

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data, error } = await supabase
        .from("content_company")
        .select("company_name");
      if (!error) {
        setCompanies(data.map((c) => c.company_name));
      }
    };

    if (isOpen) {
      fetchCompanies();
      const initialData = {
        name: "",
        nick_name: "",
        company: "",
        email: "",
        phone_number: "",
        class: "D",
        profit_share: 0,
        role: [],
        verified: false,
        date_of_birth: "",
      };

      let userData = null;
      if (user) {
        // 기존 사용자 데이터에서 역할 처리
        let processedRole = [];
        if (user.role) {
          if (Array.isArray(user.role)) {
            processedRole = user.role;
          } else if (typeof user.role === "string") {
            // 문자열에서 불필요한 문자 제거 후 배열로 변환
            const cleanRole = user.role.replace(/[\[\]"]/g, "");
            processedRole = cleanRole
              .split(",")
              .map((r) => r.trim())
              .filter((r) => r);
          }
        }
        userData = { ...user, role: processedRole };
      }

      setFormData(userData ? { ...initialData, ...userData } : initialData);
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" || type === "switch" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleRoleChange = (selectedRoles) => {
    setFormData((prev) => ({ ...prev, role: selectedRoles }));
  };

  const handleSave = async () => {
    setLoading(true);

    // 역할 데이터를 쉼표로 구분된 문자열로 변환
    const roleString =
      formData.role && formData.role.length > 0
        ? formData.role.filter((role) => role && role.trim()).join(", ")
        : "";

    if (isEditing) {
      const { id, created_date, modified_date, approved_at, ...updateData } =
        formData;
      const { error } = await supabase
        .from("user")
        .update({ ...updateData, role: roleString })
        .eq("id", user.id);
      if (error) {
        toast({
          title: "Update failed",
          description: error.message,
          status: "error",
        });
      } else {
        toast({ title: "User updated", status: "success" });
        onSave();
        onClose();
      }
    } else {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        toast({
          title: "Error creating auth user",
          description: authError.message,
          status: "error",
        });
        setLoading(false);
        return;
      }

      if (authData.user) {
        const { password, ...profileData } = formData;

        // user 테이블에 직접 데이터 삽입
        const { error: insertError } = await supabase.from("user").insert({
          id: authData.user.id,
          ...profileData,
          role: roleString,
          is_migration: true,
          created_date: new Date().toISOString(),
          modified_date: new Date().toISOString(),
        });

        if (insertError) {
          toast({
            title: "Error creating user profile",
            description: insertError.message,
            status: "error",
          });
        } else {
          toast({ title: "User created successfully", status: "success" });
          onSave();
          onClose();
        }
      }
    }
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditing ? "사용자 정보 수정" : "사용자 등록"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {!isEditing && (
              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>이메일</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>비밀번호</FormLabel>
                  <Input
                    name="password"
                    type="password"
                    value={formData.password || ""}
                    onChange={handleChange}
                  />
                </FormControl>
              </SimpleGrid>
            )}
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>이름</FormLabel>
                <Input
                  name="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>닉네임</FormLabel>
                <Input
                  name="nick_name"
                  value={formData.nick_name || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>회사</FormLabel>
                <Select
                  name="company"
                  placeholder="회사 선택"
                  value={formData.company || ""}
                  onChange={handleChange}
                >
                  {companies.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>연락처</FormLabel>
                <Input
                  name="phone_number"
                  value={formData.phone_number || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>생년월일</FormLabel>
                <Input
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth || ""}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel>등급</FormLabel>
                <Select
                  name="class"
                  value={formData.class || "D"}
                  onChange={handleChange}
                >
                  <option value="D">D</option> <option value="C">C</option>{" "}
                  <option value="B">B</option> <option value="A">A</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>수익 비율 (%)</FormLabel>
                <Input
                  name="profit_share"
                  type="number"
                  value={formData.profit_share || 0}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="verified-switch" mb="0">
                  승인됨
                </FormLabel>
                <Switch
                  id="verified-switch"
                  name="verified"
                  isChecked={formData.verified || false}
                  onChange={handleChange}
                />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>역할</FormLabel>
              <CheckboxGroup
                colorScheme="blue"
                value={formData.role || []}
                onChange={handleRoleChange}
              >
                <Stack
                  spacing={[2, 4]}
                  direction={["column", "row"]}
                  wrap="wrap"
                >
                  {ROLES.map((role) => (
                    <Checkbox key={role} value={role}>
                      {role}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={loading}>
            저장
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditUserModal;
